import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, AlertTriangle, Ban, CheckCircle2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import ExpandToggle from "../../Core/ExpandToggle/ExpandToggle";
import InvoiceBatch, { InvoiceBatchDryRun } from "../../../models/InvoiceBatch";

const DETAILS_ID = "invoice-dry-run-details";
import {
	groupDryRunFindings,
	hasPlannedWrites,
	summarizeDryRun,
} from "../../../utils/invoiceDryRun.utils";
import { translateDryRunNotChecked } from "../../../utils/invoiceDryRun.messages";
import { invoiceCustomerName } from "../../../utils/invoiceExport.utils";
import InvoiceDryRunFindingRow from "./InvoiceDryRunFindingRow";

interface Props {
	dryRun: InvoiceBatchDryRun;
	/** The batch's invoices, to name the invoices and customers findings point at. */
	invoices: InvoiceBatch["invoices"];
	/** Display name of `dryRun.startedBy`, when it could be resolved. */
	startedByName?: string;
	/** Scroll to an invoice's row in the batch table. */
	onShowInvoice?: (invoiceId: string) => void;
}

function formatDate(date?: string) {
	return date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "";
}

/**
 * Persistent result of the batch's latest dry run ("Testkör"): when it ran and
 * by whom, a summary, findings per invoice, what would be written and what
 * could not be checked. A run that could not complete (ERROR) is shown as such
 * and never as a run that found errors.
 */
const InvoiceDryRunPanel: React.FC<Props> = ({
	dryRun,
	invoices,
	startedByName,
	onShowInvoice,
}) => {
	const { t, i18n } = useTranslation();

	const report = dryRun.status === "DONE" ? dryRun.report : undefined;

	const names = useMemo(() => {
		const invoiceNames = new Map<string, string>();
		const customerNames = new Map<string, string>();

		for (const { invoice } of invoices) {
			const name = invoiceCustomerName(invoice);
			invoiceNames.set(
				invoice.id,
				invoice.customerNumber ? `${name} (${invoice.customerNumber})` : name
			);
			customerNames.set((invoice.customer as { id: string })?.id, name);
		}

		return { invoiceNames, customerNames };
	}, [invoices]);

	const grouped = useMemo(
		() =>
			report
				? groupDryRunFindings(
						report.findings ?? [],
						invoices.map(({ invoice }) => invoice.id)
				  )
				: undefined,
		[report, invoices]
	);

	const summary = useMemo(
		() => (report ? summarizeDryRun({ ...report, findings: report.findings ?? [] }) : undefined),
		[report]
	);

	const when =
		dryRun.status === "RUNNING"
			? t("invoiceBatch.dryRun.startedAt", { date: formatDate(dryRun.startedAt) })
			: startedByName
			? t("invoiceBatch.dryRun.ranAtBy", {
					date: formatDate(dryRun.finishedAt ?? dryRun.startedAt),
					name: startedByName,
			  })
			: t("invoiceBatch.dryRun.ranAt", { date: formatDate(dryRun.finishedAt ?? dryRun.startedAt) });

	let tone: "running" | "error" | "findingsError" | "findingsWarning" | "clean";
	let title: string;
	let icon: React.ReactNode;

	if (dryRun.status === "RUNNING") {
		tone = "running";
		title = t("invoiceBatch.dryRun.running");
		icon = <Loader2 size={20} className="animate-spin text-gray-500" />;
	} else if (dryRun.status === "ERROR") {
		tone = "error";
		title = t("invoiceBatch.dryRun.errorTitle");
		icon = <Ban size={20} className="text-gray-600" />;
	} else if (summary && summary.errorCount > 0) {
		tone = "findingsError";
		title = t("invoiceBatch.dryRun.resultErrors");
		icon = <AlertCircle size={20} className="text-red-600" />;
	} else if (summary && summary.warningCount > 0) {
		tone = "findingsWarning";
		title = t("invoiceBatch.dryRun.resultWarnings");
		icon = <AlertTriangle size={20} className="text-amber-600" />;
	} else {
		tone = "clean";
		title = t("invoiceBatch.dryRun.resultClean");
		icon = <CheckCircle2 size={20} className="text-green-600" />;
	}

	const toneClasses = dryRun.outdated
		? "border-gray-300 bg-white"
		: {
				running: "border-gray-300 bg-gray-50",
				error: "border-gray-400 bg-gray-50",
				findingsError: "border-red-300 bg-red-50",
				findingsWarning: "border-amber-300 bg-amber-50",
				clean: "border-green-300 bg-green-50",
		  }[tone];

	const plannedWrites = report?.plannedWrites;
	const notChecked = report?.notChecked ?? [];

	// A run with findings can be long; keep the details behind a toggle so the
	// batch content stays reachable. A clean run is short and always expanded.
	const collapsible = !!summary && summary.errorCount + summary.warningCount > 0;
	const [detailsExpanded, setDetailsExpanded] = useState(!collapsible);

	useEffect(() => {
		setDetailsExpanded(!collapsible);
	}, [collapsible, dryRun.finishedAt]);

	return (
		<section
			aria-label={t("invoiceBatch.dryRun.title")}
			data-testid="invoice-dry-run-panel"
			className={cn("mb-3 rounded-md border-2 border-solid p-4", toneClasses)}
		>
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-2.5">
					<span className="mt-0.5 shrink-0">{icon}</span>
					<div className="flex flex-col gap-0.5">
						<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
							{t("invoiceBatch.dryRun.title")}
						</span>
						<h3 className="m-0 text-base font-semibold text-gray-900">{title}</h3>
						<span className="text-xs text-gray-600">{when}</span>
					</div>
				</div>

				{dryRun.outdated && dryRun.status !== "RUNNING" && (
					<span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-solid border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
						{t("invoiceBatch.dryRun.outdated")}
					</span>
				)}
			</div>

			{dryRun.outdated && dryRun.status !== "RUNNING" && (
				<p className="m-0 mt-3 text-sm text-gray-700">{t("invoiceBatch.dryRun.outdatedInfo")}</p>
			)}

			{dryRun.status === "ERROR" && (
				<div className="mt-3 flex flex-col gap-2">
					{dryRun.errorReason && (
						<p className="m-0 whitespace-pre-wrap break-words rounded border border-solid border-gray-200 bg-white px-3 py-2 text-sm text-gray-800">
							{dryRun.errorReason}
						</p>
					)}
					<p className="m-0 text-sm text-gray-700">{t("invoiceBatch.dryRun.errorInfo")}</p>
				</div>
			)}

			{report && summary && grouped && (
				<div className={cn("mt-3 flex flex-col gap-4", dryRun.outdated && "opacity-70")}>
					<div className="flex items-center justify-between gap-4">
						<p className="m-0 text-sm text-gray-800">
							{[
								t("invoiceBatch.dryRun.invoicesChecked", { count: summary.invoicesChecked }),
								summary.invoicesWithErrors > 0 &&
									t("invoiceBatch.dryRun.invoicesWithErrors", {
										count: summary.invoicesWithErrors,
									}),
								summary.invoicesWithWarnings > 0 &&
									t("invoiceBatch.dryRun.invoicesWithWarnings", {
										count: summary.invoicesWithWarnings,
									}),
							]
								.filter(Boolean)
								.join(" · ")}
						</p>
						{collapsible && (
							<ExpandToggle
								expanded={detailsExpanded}
								onToggle={() => setDetailsExpanded((prev) => !prev)}
								controls={DETAILS_ID}
								showLabel={t("invoiceBatch.dryRun.showDetails")}
								hideLabel={t("invoiceBatch.dryRun.hideDetails")}
							/>
						)}
					</div>

					{detailsExpanded && (
						<div id={DETAILS_ID} className="flex flex-col gap-4">
							{grouped.batchLevel.length > 0 && (
								<div>
									<h4 className="m-0 mb-1 text-sm font-semibold text-gray-900">
										{t("invoiceBatch.dryRun.batchFindings")}
									</h4>
									<ul className="m-0 list-none p-0">
										{grouped.batchLevel.map((finding, index) => (
											<InvoiceDryRunFindingRow
												key={`${finding.code}-${index}`}
												finding={finding}
												context={
													finding.customerId
														? names.customerNames.get(finding.customerId)
														: undefined
												}
											/>
										))}
									</ul>
								</div>
							)}

							{grouped.byInvoice.length > 0 && (
								<div>
									<h4 className="m-0 mb-1 text-sm font-semibold text-gray-900">
										{t("invoiceBatch.dryRun.invoiceFindings")}
									</h4>
									<ul className="m-0 max-h-[40vh] list-none overflow-auto rounded-md border border-solid border-gray-200 bg-white p-0">
										{grouped.byInvoice.map((group) => (
											<li
												key={group.invoiceId}
												className="border-0 border-b border-solid border-gray-100 px-3 py-2 last:border-b-0"
											>
												<div className="flex items-center justify-between gap-3">
													<span className="text-sm font-medium text-gray-900">
														{names.invoiceNames.get(group.invoiceId) ?? group.invoiceId}
													</span>
													{onShowInvoice && names.invoiceNames.has(group.invoiceId) && (
														<button
															type="button"
															onClick={() => onShowInvoice(group.invoiceId)}
															className="shrink-0 text-xs font-medium text-blue-700 underline-offset-2 hover:underline"
														>
															{t("invoiceBatch.dryRun.showInvoice")}
														</button>
													)}
												</div>
												<ul className="m-0 list-none p-0">
													{group.findings.map((finding, index) => (
														<InvoiceDryRunFindingRow
															key={`${finding.code}-${index}`}
															finding={finding}
														/>
													))}
												</ul>
											</li>
										))}
									</ul>
								</div>
							)}

							{plannedWrites && hasPlannedWrites(report) && (
								<div>
									<h4 className="m-0 mb-1 text-sm font-semibold text-gray-900">
										{t("invoiceBatch.dryRun.plannedWrites")}
									</h4>
									<div className="flex flex-col gap-1">
										{plannedWrites.customersToCreate?.length > 0 && (
											<details className="text-sm text-gray-800">
												<summary className="cursor-pointer">
													{t("invoiceBatch.dryRun.customersToCreate", {
														count: plannedWrites.customersToCreate.length,
													})}
												</summary>
												<ul className="m-0 mt-1 list-none pl-4">
													{plannedWrites.customersToCreate.map((customer) => (
														<li
															key={`${customer.tenantId ?? ""}-${customer.id}`}
															className="py-0.5"
														>
															{customer.name} ({customer.customerNumber})
															{customer.tenantId && (
																<span className="ml-1 text-xs text-gray-500">
																	{t("invoiceBatch.dryRun.tenant", { tenantId: customer.tenantId })}
																</span>
															)}
														</li>
													))}
												</ul>
											</details>
										)}

										{plannedWrites.customersToUpdate?.length > 0 && (
											<details className="text-sm text-gray-800">
												<summary className="cursor-pointer">
													{t("invoiceBatch.dryRun.customersToUpdate", {
														count: plannedWrites.customersToUpdate.length,
													})}
												</summary>
												<ul className="m-0 mt-1 list-none pl-4">
													{plannedWrites.customersToUpdate.map((customer) => (
														<li
															key={`${customer.tenantId ?? ""}-${customer.id}`}
															className="py-0.5"
														>
															{customer.name} ({customer.customerNumber})
															{customer.tenantId && (
																<span className="ml-1 text-xs text-gray-500">
																	{t("invoiceBatch.dryRun.tenant", { tenantId: customer.tenantId })}
																</span>
															)}
															{customer.fields?.length > 0 && (
																<span className="block text-xs text-gray-600">
																	{t("invoiceBatch.dryRun.changedFields", {
																		fields: customer.fields.join(", "),
																	})}
																</span>
															)}
														</li>
													))}
												</ul>
											</details>
										)}

										{plannedWrites.articlesToCreate?.length > 0 && (
											<details className="text-sm text-gray-800">
												<summary className="cursor-pointer">
													{t("invoiceBatch.dryRun.articlesToCreate", {
														count: plannedWrites.articlesToCreate.length,
													})}
												</summary>
												<p className="m-0 mt-1 pl-4 text-sm">
													{plannedWrites.articlesToCreate.join(", ")}
												</p>
											</details>
										)}

										{plannedWrites.subaccountsToCreate?.length > 0 && (
											<details className="text-sm text-gray-800">
												<summary className="cursor-pointer">
													{t("invoiceBatch.dryRun.subaccountsToCreate", {
														count: plannedWrites.subaccountsToCreate.length,
													})}
												</summary>
												<p className="m-0 mt-1 pl-4 text-sm">
													{plannedWrites.subaccountsToCreate.join(", ")}
												</p>
											</details>
										)}
									</div>
								</div>
							)}

							{notChecked.length > 0 && (
								<div>
									<h4 className="m-0 mb-1 text-sm font-semibold text-gray-900">
										{t("invoiceBatch.dryRun.notChecked")}
									</h4>
									<ul className="m-0 list-disc pl-5 text-sm text-gray-800">
										{notChecked.map((code) => (
											<li key={code}>{translateDryRunNotChecked(code, i18n.language)}</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					<p className="m-0 text-xs text-gray-600">{t("invoiceBatch.dryRun.notAGuarantee")}</p>
				</div>
			)}
		</section>
	);
};

export default InvoiceDryRunPanel;
